/**
 * ConciergeOnButton — the one-tap turn-on for a concierge surface that is
 * sitting in its off state on an account that has already unlocked it
 * (card cEuitTMwLs6f).
 *
 * The defect this closes: the gifting concierge rides the `vault` AI
 * permission, and AI_DEFAULT_TOGGLES ships that permission off. So the screen
 * a subscriber reaches from the paywall sheet showed a dead end — "turn it on
 * in Settings → Concierge" — for the feature the sheet sold them by name. The
 * gate was never the entitlement (the server enforces Plus on the route
 * itself); it was the pen pointing somewhere else. One tap here issues the
 * same PUT /api/ai/toggles the Settings screen issues, in the context the
 * member is already reading.
 *
 * Deliberately dumb: it does not know about Plus, it stays visible on failure
 * and names the failure, and it hands the fresh state back to the caller so
 * the panel it was blocking can open without a reload.
 */
import { useState } from 'react';
import { api } from '../../shell/bridge';
// The styles travel with the button (card cEuitTMwLs6f Phase 2): a screen that
// reuses the one-tap outside Notebook gets the same look without importing
// Notebook's stylesheet.
import './concierge-on.css';

/** The AI permission categories the Settings screen can flip. Only the ones a
 *  paid surface sits behind; the list stays explicit so a typo cannot silently
 *  turn on something else. */
export type ConciergeCategory = 'vault' | 'dates' | 'digest' | 'interests' | 'recap';

/**
 * api() rejects with `Error(JSON.stringify(body))` — so a member shown
 * `e.message` reads an object. Turn that into the sentence the server wrote,
 * or the caller's own, and never echo the blob.
 */
export function humanError(e: unknown, fallback: string): string {
  const raw = e instanceof Error ? e.message : typeof e === 'string' ? e : '';
  if (!raw) return fallback;
  try {
    const parsed = JSON.parse(raw) as { error?: { message?: string }; message?: string };
    const said = parsed?.error?.message ?? parsed?.message;
    return typeof said === 'string' && said.trim() ? said : fallback;
  } catch {
    return raw.length > 140 ? fallback : raw;
  }
}

export function ConciergeOnButton({
  category = 'vault',
  label = 'Turn on the concierge',
  note,
  onOn,
  testId = 'concierge-on',
}: {
  category?: ConciergeCategory;
  label?: string;
  note?: string;
  onOn?: () => void;
  testId?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const turnOn = async () => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      await api('/api/ai/toggles', { method: 'PUT', body: { category, on: true } });
      onOn?.();
    } catch (e) {
      setErr(humanError(e, 'Could not turn the concierge on — try Settings → Concierge.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="concierge-on">
      <button
        type="button"
        className="pear-btn pear-btn-mustard concierge-on-btn"
        data-testid={testId}
        data-category={category}
        onClick={() => void turnOn()}
        disabled={busy}
      >
        {busy ? 'Turning it on…' : label}
      </button>
      {note && <span className="concierge-on-note">{note}</span>}
      {err && (
        <span className="concierge-on-err" data-testid={`${testId}-err`}>
          {err}
        </span>
      )}
    </span>
  );
}
